import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Copy, ExternalLink, Loader2, Plus, XCircle } from "lucide-react";
import {
  AWS_REGIONS,
  buildGcpSetupScript,
  AZURE_DEPLOY_IDENTITY,
  GCP_REGIONS,
  isAzureLocation,
  isAzureResourceGroup,
  isGcpProjectId,
  isGcpProjectNumber,
  type AzureCleanupReport,
  type CloudConnectionSummary,
  type CloudProvider,
  type CloudStatus,
} from "@mycompbox/shared";
import {
  useCheckAzure,
  useCloudLink,
  useCloudStatus,
  useConnectGcp,
  useGcpAuthorize,
  useRemoveCloud,
  useRenameCloud,
  useSetCloudBudget,
  useSetDefaultCloud,
} from "@/hooks/queries/use-cloud";
import { ConnectGitHubGate } from "@/components/github/connect-github-gate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PROVIDER_NAMES: Record<CloudProvider, string> = { gcp: "Google Cloud", aws: "AWS", azure: "Microsoft Azure" };

/** "Google Cloud · acme-prod", "AWS · 123456789012", "Microsoft Azure · my-group". */
function connectionLabel(c: CloudConnectionSummary): string {
  const where = c.provider === "azure" ? (c.resourceGroup ?? c.projectId) : c.projectId;
  return `${PROVIDER_NAMES[c.provider]} · ${where}`;
}

/** Each connection's connectedAt for one provider: a change means a stack or deployment reported back. */
function providerSnapshot(connections: CloudConnectionSummary[], provider: CloudProvider): string {
  return connections
    .filter((x) => x.provider === provider)
    .map((x) => `${x.id}@${x.connectedAt}`)
    .sort()
    .join("|");
}

interface DisconnectNotice {
  text: string | null;
  leftBehind: string[];
  retained: string[];
}

/** What to tell the admin after Disconnect; null when there is nothing to say. */
function disconnectNotice(name: string, skipped: string | null, azure: AzureCleanupReport | undefined): DisconnectNotice | null {
  const leftBehind = azure?.leftBehind ?? [];
  const retained = azure?.retained ?? [];
  if (!skipped && leftBehind.length === 0 && retained.length === 0) return null;
  return { text: skipped ? `${name} was disconnected from CompBox. ${skipped}` : `${name} was disconnected from CompBox.`, leftBehind, retained };
}

/**
 * Settings → Cloud: the organization's clouds. An org admin connects one or
 * more — Google Cloud, AWS or Azure, one click each — and picks the default
 * that every project deploys to unless it chooses another. Keyless: no keys
 * or passwords are stored.
 */
export function CloudCard() {
  const [waitingFor, setWaitingFor] = useState<{ provider: CloudProvider; since: string } | null>(null);
  const { data: status, isLoading } = useCloudStatus(waitingFor !== null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<DisconnectNotice | null>(null);
  const connections = useMemo(() => status?.connections ?? [], [status]);

  // The Azure wizard keeps itself open after the deployment reports back (it still has to test the connection).
  const azureConnected = useMemo(
    () =>
      waitingFor?.provider === "azure" && providerSnapshot(connections, "azure") !== waitingFor.since
        ? ([...connections].filter((x) => x.provider === "azure").sort((a, b) => b.connectedAt.localeCompare(a.connectedAt))[0] ?? null)
        : null,
    [connections, waitingFor]
  );

  // The AWS stack reports back to CompBox; stop waiting once it has.
  useEffect(() => {
    if (waitingFor && waitingFor.provider !== "azure" && providerSnapshot(connections, waitingFor.provider) !== waitingFor.since) {
      setWaitingFor(null);
      setAdding(false);
    }
  }, [connections, waitingFor]);

  if (isLoading || !status) return <div className="h-16 animate-pulse rounded-lg bg-muted" />;

  const noticeEl = notice ? (
    <div role="status" className="space-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
      {notice.text ? <p>{notice.text}</p> : null}
      {notice.leftBehind.length > 0 ? (
        <>
          <p>Still in Azure or GitHub (CompBox couldn't remove these):</p>
          <ul aria-label="Left behind" className="list-disc pl-5">
            {notice.leftBehind.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ) : null}
      {notice.retained.length > 0 ? (
        <>
          <p>Kept on purpose (yours):</p>
          <ul aria-label="Retained" className="list-disc pl-5">
            {notice.retained.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  ) : null;

  if (connections.length > 0 && !adding && waitingFor?.provider !== "azure") {
    return (
      <div className="space-y-3">
        {noticeEl}
        <ul className="space-y-2" aria-label="Cloud connections">
          {connections.map((c) => (
            <CloudConnectionRow key={c.id} connection={c} canManage={status.canManage} isOrgAdmin={status.isOrgAdmin} isLast={connections.length === 1} onDisconnected={(name, skipped, azure) => { setNotice(disconnectNotice(name, skipped, azure)); }} />
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          Projects deploy to the default cloud unless they choose another.
          {status.canManage ? " If you connect another GitHub account, connect your clouds again so it's trusted too." : ""}
        </p>
        {connections.some((c) => c.provider !== "gcp") ? (
          <p className="text-xs text-muted-foreground">Project stacks deploy to Google Cloud and Azure for now; AWS stacks are coming next.</p>
        ) : null}
        {status.canManage ? (
          <Button size="sm" variant="outline" onClick={() => { setAdding(true); }}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add cloud
          </Button>
        ) : null}
      </div>
    );
  }

  if (!status.canManage) {
    return (
      <>
        {noticeEl}
        <p className="text-sm text-muted-foreground">Your organization admin hasn't connected a cloud yet.</p>
      </>
    );
  }

  if (status.githubOwners.length === 0) {
    return <ConnectGitHubGate returnTo="/settings#cloud" reason="so deploys can run from your GitHub repositories" />;
  }

  return (
    <>
      {noticeEl}
    <ConnectCloud
      status={status}
      initial={null}
      waitingFor={waitingFor?.provider ?? null}
      azureConnected={azureConnected}
      onWaiting={(provider) => { setWaitingFor({ provider, since: providerSnapshot(connections, provider) }); }}
      onDone={() => {
        setWaitingFor(null);
        setAdding(false);
      }}
      onCancel={
        connections.length > 0
          ? () => {
              setWaitingFor(null);
              setAdding(false);
            }
          : undefined
      }
    />
    </>
  );
}

/** One connection: name, provider, region and default badge; managers rename, make default and remove. */
function CloudConnectionRow({
  connection: c,
  canManage,
  isOrgAdmin,
  isLast,
  onDisconnected,
}: {
  connection: CloudConnectionSummary;
  canManage: boolean;
  isOrgAdmin: boolean;
  isLast: boolean;
  onDisconnected: (name: string, skipped: string | null, azure?: AzureCleanupReport) => void;
}) {
  const rename = useRenameCloud();
  const setDefault = useSetDefaultCloud();
  const remove = useRemoveCloud();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const [confirming, setConfirming] = useState(false);
  const [cleanupFailure, setCleanupFailure] = useState<AzureCleanupReport | null>(null);

  const trimmed = name.trim();
  const nameOk = trimmed.length >= 1 && trimmed.length <= 100;

  const startEditing = () => {
    setName(c.name);
    rename.reset();
    setEditing(true);
  };
  const save = () => {
    if (!nameOk) return;
    if (trimmed === c.name) {
      setEditing(false);
      return;
    }
    rename.mutate({ id: c.id, name: trimmed }, { onSuccess: () => { setEditing(false); } });
  };

  return (
    <li className="space-y-1 rounded-lg border border-white/[0.08] p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
          {editing ? (
            <Input
              autoFocus
              aria-label="Connection name"
              className="h-8 w-48"
              value={name}
              maxLength={100}
              disabled={rename.isPending}
              onChange={(e) => { setName(e.target.value); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setEditing(false);
                }
              }}
            />
          ) : (
            <span className="font-medium">{c.name}</span>
          )}
          {c.isDefault ? <Badge variant="secondary">Default</Badge> : null}
          <span className="text-xs text-muted-foreground">
            {connectionLabel(c)} · {c.region}
          </span>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {editing ? (
            <>
              <Button size="sm" disabled={!nameOk || rename.isPending} onClick={save}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); }}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button asChild size="sm" variant="outline">
                <a href={c.consoleUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  Open console
                </a>
              </Button>
              {canManage ? (
                <>
                  <Button size="sm" variant="outline" aria-label={`Rename ${c.name}`} onClick={startEditing}>
                    Rename
                  </Button>
                  {!c.isDefault ? (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Make ${c.name} the default`}
                      disabled={setDefault.isPending}
                      onClick={() => { setDefault.mutate(c.id); }}
                    >
                      Make default
                    </Button>
                  ) : null}
                  <Button size="sm" variant="outline" aria-label={`Disconnect ${c.name}`} onClick={() => { remove.reset(); setCleanupFailure(null); setConfirming(true); }}>
                    Disconnect
                  </Button>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      {c.serviceAccountEmail ? <p className="text-xs text-muted-foreground">Deploys run as {c.serviceAccountEmail}.</p> : null}
      {rename.error ? <p className="text-xs text-destructive">{rename.error.message}</p> : null}
      <ConnectionLiveState connection={c} isOrgAdmin={isOrgAdmin} />
      {setDefault.error ? <p className="text-xs text-destructive">{setDefault.error.message}</p> : null}

      <Dialog open={confirming} onOpenChange={(open) => { if (!remove.isPending) setConfirming(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {c.name}?</DialogTitle>
            <DialogDescription>
              {c.provider === "gcp"
                ? "CompBox will remove its deploy account and trust settings from your Google Cloud project. Your project, app, database and state bucket stay as they are. "
                : c.provider === "azure"
                  ? "CompBox will remove its role assignments and federated credentials from Azure and its variables from your GitHub repositories. Your resource group, app, database and state storage stay as they are. "
                  : ""}
              {isLast
                ? "This is your organization's only cloud: projects will have no cloud to deploy to until you connect one."
                : "Projects using this connection will fall back to the organization default."}
            </DialogDescription>
          </DialogHeader>
          {remove.error ? <p className="text-sm text-destructive">{remove.error.message}</p> : null}
          {cleanupFailure ? (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              <p>{c.name} was not disconnected: {cleanupFailure.failure ?? "Azure cleanup failed."} You can try again.</p>
              {cleanupFailure.leftBehind.length > 0 ? (
                <ul aria-label="Left behind" className="list-disc pl-5">
                  {cleanupFailure.leftBehind.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" disabled={remove.isPending} onClick={() => { setConfirming(false); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                setCleanupFailure(null);
                remove.mutate(c.id, {
                  onSuccess: (res) => {
                    if (res.data.azure && !res.data.azure.connectionRemoved) {
                      setCleanupFailure(res.data.azure);
                      return;
                    }
                    setConfirming(false);
                    onDisconnected(c.name, res.data.skipped, res.data.azure);
                  },
                });
              }}
            >
              {remove.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
}

function ConnectCloud({
  status,
  initial,
  waitingFor,
  azureConnected,
  onWaiting,
  onDone,
  onCancel,
}: {
  status: CloudStatus;
  initial: CloudConnectionSummary | null;
  waitingFor: CloudProvider | null;
  /** The Azure connection that reported back while the wizard waited. */
  azureConnected: CloudConnectionSummary | null;
  onWaiting: (provider: CloudProvider) => void;
  onDone: () => void;
  /** Back to the list; absent when there's nothing to go back to. */
  onCancel?: (() => void) | undefined;
}) {
  const [provider, setProvider] = useState<CloudProvider>(initial?.provider ?? "gcp");
  const owners = status.githubOwners.join(", ");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Connect your cloud once and every project can deploy to it. Nothing secret is stored: GitHub Actions in your
        repositories sign in to the cloud directly, and only from {owners}.
      </p>

      <div className="grid grid-cols-3 gap-2" role="tablist" aria-label="Cloud provider">
        {(Object.keys(PROVIDER_NAMES) as CloudProvider[]).map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={provider === p}
            onClick={() => { setProvider(p); }}
            className={cn(
              "rounded-lg border px-3 py-2 text-sm transition-colors",
              provider === p ? "border-primary bg-primary/10 font-medium" : "border-white/[0.08] text-muted-foreground hover:bg-white/[0.04]"
            )}
          >
            {PROVIDER_NAMES[p]}
          </button>
        ))}
      </div>

      {provider === "gcp" ? (
        <ConnectGcp githubOwners={status.githubOwners} oneClick={status.oneClick.gcp} initial={initial?.provider === "gcp" ? initial : null} onDone={onDone} />
      ) : provider === "azure" ? (
        <AzureWizard
          available={status.oneClick.azure}
          githubOwners={status.githubOwners}
          waiting={waitingFor === "azure"}
          connected={azureConnected}
          onWaiting={() => { onWaiting("azure"); }}
          onDone={onDone}
        />
      ) : (
        <ConnectByLink
          provider={provider}
          available={status.oneClick[provider]}
          waiting={waitingFor === provider}
          onWaiting={() => { onWaiting(provider); }}
        />
      )}

      {onCancel ? (
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      ) : null}
    </div>
  );
}

const AZURE_STEPS = ["Sign in", "Subscription & region", "Review", "Connect", "Test"] as const;

/**
 * Azure guided connection (ENG-462): sign in, choose subscription and region, review what will be created,
 * connect (the "Deploy to Azure" template) and test the new connection.
 */
function AzureWizard({
  available,
  githubOwners,
  waiting,
  connected,
  onWaiting,
  onDone,
}: {
  available: boolean;
  githubOwners: string[];
  waiting: boolean;
  connected: CloudConnectionSummary | null;
  onWaiting: () => void;
  onDone: () => void;
}) {
  const link = useCloudLink();
  const check = useCheckAzure();
  const [step, setStep] = useState(0);
  const [resourceGroup, setResourceGroup] = useState("compbox");
  const [region, setRegion] = useState("eastus");

  // Once the deployment reports back, go straight to the test.
  useEffect(() => {
    if (connected) setStep(4);
  }, [connected]);

  if (!available) {
    return <p className="text-sm text-muted-foreground">One-click {PROVIDER_NAMES.azure} isn't set up on this CompBox server yet.</p>;
  }

  const rgOk = isAzureResourceGroup(resourceGroup.trim());
  const regionOk = isAzureLocation(region.trim());
  const canNext = step === 1 ? rgOk && regionOk : step === 3 ? connected !== null : true;

  async function open() {
    // Open the tab now, while the click still counts, so pop-up blockers allow it.
    const tab = window.open("", "_blank");
    try {
      const url = await link.mutateAsync({ provider: "azure" });
      if (tab) tab.location.href = url;
      else window.location.href = url;
      onWaiting();
    } catch {
      tab?.close();
    }
  }

  return (
    <div className="space-y-4">
      <ol className="flex flex-wrap gap-2 text-xs" aria-label="Azure connection steps">
        {AZURE_STEPS.map((label, i) => (
          <li
            key={label}
            aria-current={i === step ? "step" : undefined}
            className={cn("rounded-full border px-2.5 py-1", i === step ? "border-primary bg-primary/10 font-medium" : "border-white/[0.08] text-muted-foreground")}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      {step === 0 ? (
        <div className="space-y-2" aria-label="Step 1: Sign in">
          <p className="text-sm text-muted-foreground">
            You sign in to Azure yourself, in the Azure portal, with an account that is an Owner of the resource group you'll use. CompBox
            never sees your Azure password and stores no secret.
          </p>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="space-y-3" aria-label="Step 2: Subscription and region">
          <p className="text-sm text-muted-foreground">
            The portal asks which subscription to use; pick the one you want deploys to run in. Tell CompBox the resource group and region
            you'll pick there so the review matches.
          </p>
          <div className="max-w-xs space-y-1.5">
            <Label htmlFor="azure-rg">Resource group</Label>
            <Input id="azure-rg" value={resourceGroup} onChange={(e) => { setResourceGroup(e.target.value); }} />
            {!rgOk ? <p className="text-xs text-destructive">Use letters, numbers, dashes, underscores, dots or parentheses.</p> : null}
          </div>
          <div className="max-w-xs space-y-1.5">
            <Label htmlFor="azure-region">Region</Label>
            <Input id="azure-region" placeholder="eastus" value={region} onChange={(e) => { setRegion(e.target.value); }} />
            {!regionOk ? <p className="text-xs text-destructive">Enter an Azure region name such as eastus or westeurope.</p> : null}
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-2" aria-label="Step 3: Review">
          <p className="text-sm text-muted-foreground">
            In resource group <span className="font-medium text-foreground">{resourceGroup.trim()}</span> ({region.trim()}) the deployment creates:
          </p>
          <ul aria-label="Resources that will be created" className="list-disc space-y-1 pl-5 text-sm">
            <li>A managed identity named {AZURE_DEPLOY_IDENTITY}</li>
            {githubOwners.map((o) => (
              <li key={o}>A federated credential trusting GitHub account {o}</li>
            ))}
            <li>A Contributor role assignment for that identity on the resource group</li>
            <li>A storage account with a tfstate container for infrastructure state</li>
            <li>A Storage Blob Data Contributor role assignment on that storage account</li>
          </ul>
          <p className="text-xs text-muted-foreground">Nothing is created until you click Create in the Azure portal.</p>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="space-y-3" aria-label="Step 4: Connect">
          <p className="text-sm text-muted-foreground">
            Opens the Azure portal with CompBox's setup filled in. Pick the subscription and resource group, then click{" "}
            <span className="font-medium text-foreground">Review + create</span>.
          </p>
          {link.error ? <p className="text-sm text-destructive">{link.error.message}</p> : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={link.isPending} onClick={() => { void open(); }}>
              <ExternalLink className="mr-1 h-3.5 w-3.5" />
              Deploy to Azure
            </Button>
            {waiting && !connected ? (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Waiting for Microsoft Azure… this updates by itself when it's done (about 2 minutes).
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === 4 ? (
        <div className="space-y-3" aria-label="Step 5: Test">
          {connected ? (
            <>
              <p className="text-sm text-muted-foreground">
                {connected.name} is connected. Test that Azure accepts CompBox's sign-in, GitHub variables sync, and spend can be read.
              </p>
              {check.error ? <p className="text-sm text-destructive">{check.error.message}</p> : null}
              {check.data ? (
                <ul aria-label="Check results" className="space-y-1 text-sm">
                  {check.data.checks.map((r) => (
                    <li key={r.name} className="flex items-start gap-2">
                      {r.status === "failed" ? <XCircle className="mt-0.5 h-4 w-4 text-destructive" /> : <CheckCircle2 className={cn("mt-0.5 h-4 w-4", r.status === "ok" ? "text-emerald-400" : "text-muted-foreground")} />}
                      <span>
                        <span className="font-medium">{CHECK_LABELS[r.name]}</span> ({r.status}): {r.message}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Button size="sm" variant="outline" disabled={check.isPending} onClick={() => { check.mutate(connected.id); }}>
                {check.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                {check.data ? "Run checks again" : "Run checks"}
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Connect first, then test.</p>
          )}
        </div>
      ) : null}

      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={step === 0} onClick={() => { setStep((s) => s - 1); }}>
          Back
        </Button>
        {step < AZURE_STEPS.length - 1 ? (
          <Button size="sm" disabled={!canNext} onClick={() => { setStep((s) => s + 1); }}>
            Next
          </Button>
        ) : (
          <Button size="sm" disabled={!connected} onClick={onDone}>
            Finish
          </Button>
        )}
      </div>
    </div>
  );
}

const CHECK_LABELS = { tokenExchange: "Token exchange", variableSync: "Variable sync", spendRead: "Spend read" } as const;

/** AWS: open the cloud's own create page; it reports back to CompBox. */
function ConnectByLink({
  provider,
  available,
  waiting,
  onWaiting,
}: {
  provider: "aws" | "azure";
  available: boolean;
  waiting: boolean;
  onWaiting: () => void;
}) {
  const link = useCloudLink();
  const [region, setRegion] = useState<string>("us-east-1");

  if (!available) {
    return <p className="text-sm text-muted-foreground">One-click {PROVIDER_NAMES[provider]} isn't set up on this CompBox server yet.</p>;
  }

  async function open() {
    // Open the tab now, while the click still counts, so pop-up blockers allow it.
    const tab = window.open("", "_blank");
    try {
      const url = await link.mutateAsync(provider === "aws" ? { provider, region } : { provider });
      if (tab) tab.location.href = url;
      else window.location.href = url;
      onWaiting();
    } catch {
      tab?.close();
    }
  }

  return (
    <div className="space-y-3">
      {provider === "aws" ? (
        <>
          <p className="text-sm text-muted-foreground">
            Opens your AWS console with CompBox's setup filled in. Click <span className="font-medium text-foreground">Create stack</span>{" "}
            there (tick the box that it creates IAM roles). It makes a deploy role that only your GitHub can use.
          </p>
          <div className="max-w-xs space-y-1.5">
            <Label htmlFor="aws-region">Region</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger id="aws-region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AWS_REGIONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Opens the Azure portal with CompBox's setup filled in. Pick a subscription and resource group, then click{" "}
          <span className="font-medium text-foreground">Review + create</span>. You need to be an Owner of that resource group.
        </p>
      )}

      {link.error ? <p className="text-sm text-destructive">{link.error.message}</p> : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={link.isPending} onClick={() => { void open(); }}>
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          {provider === "aws" ? "Connect AWS" : "Deploy to Azure"}
        </Button>
        {waiting ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Waiting for {PROVIDER_NAMES[provider]}… this updates by itself when it's done (about 2 minutes).
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ConnectGcp({
  githubOwners,
  oneClick,
  initial,
  onDone,
}: {
  githubOwners: string[];
  oneClick: boolean;
  initial: { projectId: string; projectNumber: string; region: string } | null;
  onDone: () => void;
}) {
  const authorize = useGcpAuthorize();
  const [projectId, setProjectId] = useState(initial?.projectId ?? "");
  const [region, setRegion] = useState(initial?.region ?? "us-central1");
  const [byScript, setByScript] = useState(!oneClick);
  const idOk = isGcpProjectId(projectId.trim());

  async function signIn() {
    const url = await authorize.mutateAsync({ projectId: projectId.trim(), region });
    window.location.assign(url);
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="gcp-project">Google Cloud project ID</Label>
          <Input id="gcp-project" placeholder="acme-prod" value={projectId} onChange={(e) => { setProjectId(e.target.value); }} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="gcp-region">Region</Label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger id="gcp-region">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GCP_REGIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {byScript ? (
        <GcpScript projectId={projectId.trim()} idOk={idOk} region={region} githubOwners={githubOwners} initialNumber={initial?.projectNumber ?? ""} onDone={onDone} />
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Sign in with Google and approve. CompBox sets everything up once with that sign-in, then throws it away.
          </p>
          {authorize.error ? <p className="text-sm text-destructive">{authorize.error.message}</p> : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={!idOk || authorize.isPending} onClick={() => { void signIn(); }}>
              Connect Google Cloud
            </Button>
            <button type="button" className="text-xs text-muted-foreground underline" onClick={() => { setByScript(true); }}>
              Prefer to run the setup yourself?
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The fallback: run CompBox's script in Cloud Shell, then paste the project number. */
function GcpScript({
  projectId,
  idOk,
  region,
  githubOwners,
  initialNumber,
  onDone,
}: {
  projectId: string;
  idOk: boolean;
  region: string;
  githubOwners: string[];
  initialNumber: string;
  onDone: () => void;
}) {
  const connect = useConnectGcp();
  const [projectNumber, setProjectNumber] = useState(initialNumber);
  const [copied, setCopied] = useState(false);
  const script = useMemo(() => (idOk ? buildGcpSetupScript({ projectId, region, githubOwners }) : null), [idOk, projectId, region, githubOwners]);

  async function save() {
    await connect.mutateAsync({ projectId, projectNumber: projectNumber.trim(), region });
    onDone();
  }

  return (
    <div className="space-y-4">
      {script ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label>Run this in Google Cloud Shell</Label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(script).then(() => { setCopied(true); });
                }}
              >
                <Copy className="mr-1 h-3.5 w-3.5" />
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button asChild size="sm" variant="outline">
                <a href={`https://shell.cloud.google.com/?project=${encodeURIComponent(projectId)}`} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  Open Cloud Shell
                </a>
              </Button>
            </div>
          </div>
          <pre className="max-h-48 overflow-auto rounded-lg bg-black/40 p-3 text-xs">{script}</pre>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="gcp-number">Project number (the script prints it)</Label>
        <Input
          id="gcp-number"
          inputMode="numeric"
          placeholder="123456789012"
          value={projectNumber}
          onChange={(e) => { setProjectNumber(e.target.value); }}
        />
      </div>

      {connect.error ? <p className="text-sm text-destructive">{connect.error.message}</p> : null}
      <Button disabled={!idOk || !isGcpProjectNumber(projectNumber.trim()) || connect.isPending} onClick={() => { void save(); }}>
        Save Google Cloud project
      </Button>
    </div>
  );
}

function usd(n: number): string {
  return `$${n.toFixed(2)}`;
}

const LEVEL_LABELS = { ok: "On track", near: "Near the cap", over: "Over the cap", unknown: "Spend level unknown" } as const;

/** Google Cloud: the deployed app's run.app address and spend against the budget. Azure: spend, level and the admin's cap. */
function ConnectionLiveState({ connection: c, isOrgAdmin }: { connection: CloudConnectionSummary; isOrgAdmin: boolean }) {
  if (c.provider === "azure") return <AzureSpend connection={c} isOrgAdmin={isOrgAdmin} />;
  if (c.provider !== "gcp") return null;
  const over = c.budgetUsd != null && c.monthSpendUsd != null && c.monthSpendUsd >= c.budgetUsd;
  const near = !over && c.budgetUsd != null && c.monthSpendUsd != null && c.monthSpendUsd >= c.budgetUsd * 0.8;
  return (
    <div className="space-y-0.5 pl-6 text-xs text-muted-foreground" aria-label={`Live state of ${c.name}`}>
      <p>
        Connected
        {c.liveUrl ? (
          <>
            {" · "}
            <a href={c.liveUrl} target="_blank" rel="noreferrer" className="underline">
              {c.liveUrl.replace(/^https?:\/\//, "")}
            </a>
          </>
        ) : (
          " · no app deployed yet"
        )}
      </p>
      <p className={over ? "text-destructive" : near ? "text-amber-300" : undefined}>{spendText(c)}</p>
    </div>
  );
}

function spendText(c: CloudConnectionSummary): string {
  return c.monthSpendUsd != null && c.budgetUsd != null
    ? `${usd(c.monthSpendUsd)} of ${usd(c.budgetUsd)} budget this month`
    : c.budgetUsd != null
      ? `Budget ${usd(c.budgetUsd)} a month · spend not read yet`
      : c.monthSpendUsd != null
        ? `${usd(c.monthSpendUsd)} spent this month · no cap set`
        : "Spend not read yet";
}

/** Azure: spend this month, its level against the cap, and (org admins) the cap control. */
function AzureSpend({ connection: c, isOrgAdmin }: { connection: CloudConnectionSummary; isOrgAdmin: boolean }) {
  const setBudget = useSetCloudBudget();
  const [value, setValue] = useState(c.budgetUsd != null ? String(c.budgetUsd) : "");
  const parsed = Number(value);
  const valueOk = value.trim() !== "" && Number.isInteger(parsed) && parsed > 0;
  const level = c.spendLevel ?? "unknown";

  return (
    <div className="space-y-1 pl-6 text-xs text-muted-foreground" aria-label={`Live state of ${c.name}`}>
      <p className={level === "over" ? "text-destructive" : level === "near" ? "text-amber-300" : undefined}>
        {spendText(c)} · {LEVEL_LABELS[level]}
      </p>
      {isOrgAdmin ? (
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={`cap-${c.id}`} className="text-xs">
            Monthly cap (USD)
          </Label>
          <Input
            id={`cap-${c.id}`}
            className="h-7 w-24"
            inputMode="numeric"
            value={value}
            onChange={(e) => { setValue(e.target.value); }}
          />
          <Button size="sm" variant="outline" disabled={!valueOk || setBudget.isPending} onClick={() => { setBudget.mutate({ id: c.id, budgetUsd: parsed }); }}>
            Set cap
          </Button>
          {c.budgetUsd != null ? (
            <Button size="sm" variant="outline" disabled={setBudget.isPending} onClick={() => { setValue(""); setBudget.mutate({ id: c.id, budgetUsd: null }); }}>
              Clear cap
            </Button>
          ) : null}
        </div>
      ) : null}
      {setBudget.error ? <p className="text-destructive">{setBudget.error.message}</p> : null}
    </div>
  );
}
